import { Link } from "react-router-dom";
import { Mail, Globe, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import WhatsAppIcon from "@/components/WhatsAppIcon";

export default function Footer() {
    return (
        <footer className="bg-[color:var(--gsf-green-900)] text-white/90 mt-24" data-testid="site-footer">
            <div className="gsf-container py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                    <div className="flex items-center gap-3 mb-5">
                        <Logo variant="full" size={80} className="rounded-lg bg-white p-1" />
                    </div>
                    <p className="text-sm leading-relaxed text-white/70 max-w-sm">
                        Global Seed Foundation strengthens communities through practical empowerment,
                        humanitarian support, inclusive development and meaningful connections between
                        people, skills, services and opportunities.
                    </p>
                    <p className="mt-6 text-xs uppercase tracking-[0.28em] text-[color:var(--gsf-gold-500)]">
                        Empowering Communities. Growing Opportunity.
                    </p>
                </div>

                <div className="md:col-span-2">
                    <h4 className="font-serif text-white text-lg mb-4">Explore</h4>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/about" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-about">About Us</Link></li>
                        <li><Link to="/programmes" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-programmes">Programmes</Link></li>
                        <li><Link to="/impact-hub" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-impact-hub">Impact Hub</Link></li>
                        <li><Link to="/impact" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-impact">Our Impact</Link></li>
                        <li><Link to="/warren-park-connect" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-wpc">Warren Park Connect</Link></li>
                        <li><Link to="/success-stories" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-success-stories">Success Stories</Link></li>
                        <li><Link to="/media" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-media">Media Centre</Link></li>
                        <li><Link to="/news" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-news">News & Stories</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <h4 className="font-serif text-white text-lg mb-4">Engage</h4>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/get-involved" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-get-involved">Volunteer</Link></li>
                        <li><Link to="/get-involved" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-partner">Partner</Link></li>
                        <li><Link to="/donate" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-donate">Donate</Link></li>
                        <li><Link to="/contact" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-contact">Contact</Link></li>
                        <li>
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/public/impact-snapshot.pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-[color:var(--gsf-gold-500)]"
                                data-testid="footer-impact-snapshot"
                            >
                                Download Impact Snapshot (PDF)
                            </a>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <h4 className="font-serif text-white text-lg mb-4">Contact</h4>
                    <ul className="space-y-3 text-sm">
                        <li className="flex items-start gap-2">
                            <Mail size={16} className="mt-0.5 text-[color:var(--gsf-gold-500)]" />
                            <a href="mailto:impact@globalseedzim.org" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-email">
                                impact@globalseedzim.org
                            </a>
                        </li>
                        <li className="flex items-start gap-2">
                            <WhatsAppIcon size={16} className="mt-0.5 text-[color:var(--gsf-gold-500)]" />
                            <a
                                href="https://wa.me/263782658473"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Chat with Global Seed Foundation on WhatsApp"
                                className="hover:text-[color:var(--gsf-gold-500)]"
                                data-testid="footer-whatsapp"
                            >
                                WhatsApp: +263 78 265 8473
                            </a>
                        </li>
                        <li className="flex items-start gap-2">
                            <Globe size={16} className="mt-0.5 text-[color:var(--gsf-gold-500)]" />
                            <span>www.globalseedzim.org</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <MapPin size={16} className="mt-0.5 text-[color:var(--gsf-gold-500)]" />
                            <span>Warren Park Constituency and surrounding communities, Harare, Zimbabwe</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="gsf-container py-6 text-xs text-white/60 flex flex-col md:flex-row justify-between gap-4">
                    <div>© 2026 Global Seed Foundation. All rights reserved.</div>
                    <div className="flex gap-5">
                        <Link to="/privacy" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-privacy">Privacy Policy</Link>
                        <Link to="/safeguarding" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-safeguarding">Safeguarding</Link>
                        <Link to="/cookie-policy" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-cookies">Cookies</Link>
                        <Link to="/terms" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-terms">Terms</Link>
                        <Link to="/donate" className="hover:text-[color:var(--gsf-gold-500)]" data-testid="footer-donation-info">Donation Info</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
